import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { Volume2, Square, BookOpen } from 'lucide-react';
import { PageWrapper } from '@/layout/components/PageWrapper.tsx';
import { PageHeader } from '@/layout/components/PageHeader.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Label } from '@/components/ui/label.tsx';
import { Textarea } from '@/components/ui/textarea.tsx';
import { AudioPlayer } from '@/components/ui/audio-player.tsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils.ts';
import {
  useGenerateSpeechMutation,
  TTS_VOICE_OPTIONS,
  TTS_MODEL_OPTIONS,
  TTS_MOOD_OPTIONS,
  TTS_VOICE_SAMPLE_URL,
  TTS_AUDIO_TAGS,
  estimateTtsUnits,
  type TtsVoice,
  type TtsModel,
  type TtsMood,
} from '@/store/textToSpeechApi.ts';

const MAX_TEXT_LENGTH = 4000;
const MAX_CUSTOM_PROMPT_LENGTH = 500;

function base64ToBlobUrl(base64: string, mimeType: string): string {
  const byteChars = atob(base64);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    byteNumbers[i] = byteChars.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType });
  return URL.createObjectURL(blob);
}

export default function TextToSpeechPage() {
  const [generateSpeech, { isLoading }] = useGenerateSpeechMutation();

  const [text, setText] = useState('');
  const [voice, setVoice] = useState<TtsVoice>('Alnilam');
  const [model, setModel] = useState<TtsModel>('gemini-3.1-flash-tts-preview');
  const [mood, setMood] = useState<TtsMood>('regular');
  const [customPrompt, setCustomPrompt] = useState('');

  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [resultInfo, setResultInfo] = useState<{
    durationSeconds: number;
    units: number;
    promptTokens: number;
    candidatesTokens: number;
    totalTokens: number;
  } | null>(null);

  const audioUrlRef = useRef<string | null>(null);
  const sampleAudioRef = useRef<HTMLAudioElement | null>(null);
  const [playingSampleVoice, setPlayingSampleVoice] = useState<TtsVoice | null>(null);

  const canSubmit = useMemo(() => {
    if (!text.trim()) {
      return false;
    }
    if (mood === 'custom' && !customPrompt.trim()) {
      return false;
    }
    return true;
  }, [text, mood, customPrompt]);

  const estimatedUnits = useMemo(() => estimateTtsUnits(text.length), [text.length]);

  async function handleSubmit() {
    if (!text.trim()) {
      toast.error('יש להזין טקסט להמרה');
      return;
    }
    if (mood === 'custom' && !customPrompt.trim()) {
      toast.error('יש להזין הנחיית אווירה מותאמת אישית');
      return;
    }

    try {
      const result = await generateSpeech({
        text: text.trim(),
        voice,
        model,
        mood,
        ...(mood === 'custom' ? { custom_prompt: customPrompt.trim() } : {}),
      }).unwrap();

      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
      }
      const url = base64ToBlobUrl(result.audio_base64, result.mime_type || 'audio/wav');
      audioUrlRef.current = url;
      setAudioUrl(url);
      setResultInfo({
        durationSeconds: result.duration_seconds,
        units: result.units,
        promptTokens: result.prompt_tokens,
        candidatesTokens: result.candidates_tokens,
        totalTokens: result.total_tokens,
      });
      toast.success('הדיבור נוצר בהצלחה');
    } catch (err: unknown) {
      const msg =
        (err as { data?: { msg?: string } })?.data?.msg ||
        'אירעה שגיאה ביצירת הדיבור';
      toast.error(msg);
    }
  }

  function handleDownload() {
    if (!audioUrl) {
      return;
    }
    const a = document.createElement('a');
    a.href = audioUrl;
    a.download = `text-to-speech-${Date.now()}.wav`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  function toggleVoiceSample(sampleVoice: TtsVoice) {
    if (playingSampleVoice === sampleVoice) {
      sampleAudioRef.current?.pause();
      setPlayingSampleVoice(null);
      return;
    }
    if (sampleAudioRef.current) {
      sampleAudioRef.current.pause();
    }
    const audio = new Audio(TTS_VOICE_SAMPLE_URL[sampleVoice]);
    audio.onended = () => setPlayingSampleVoice(null);
    audio.onerror = () => {
      setPlayingSampleVoice(null);
      toast.error('לא ניתן להשמיע את דוגמת הקול');
    };
    sampleAudioRef.current = audio;
    setPlayingSampleVoice(sampleVoice);
    audio.play();
  }

  function insertAudioTag(tag: string) {
    setText((prev) => {
      const separator = prev && !prev.endsWith(' ') && !prev.endsWith('\n') ? ' ' : '';
      return `${prev}${separator}${tag} `.slice(0, MAX_TEXT_LENGTH);
    });
  }

  return (
    <PageWrapper>
      <PageHeader title="יצירת קול מטקסט" />

      <Card className="max-w-3xl w-full">
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle>המרת טקסט לדיבור</CardTitle>
              <CardDescription>
                בחרו קול, אווירת דיבור ומודל, הזינו טקסט וקבלו קובץ שמע מוכן להאזנה ולהורדה.
              </CardDescription>
              <p className="text-xs text-muted-foreground mt-2">
                עלות יצירת הקול משתנה בהתאם לפרמטרים שונים (כגון אורך הטקסט והמודל שנבחר) - להלן מוצגת עלות
                משוערת בלבד, כאשר העלות הסופית נקבעת על ידי מנוע יצירת הקול של החברה החיצונית המפעילה את השירות.
                יש לשים לב כי התמחור הנוכחי הינו זמני, וייתכנו שינויים בתמחור בקרוב.
              </p>
            </div>
            <Button type="button" variant="outline" size="sm" asChild className="shrink-0">
              <Link to="/tools/text-to-speech/docs">
                <BookOpen className="size-4 ml-1" />
                מדריך מורחב ותגיות
              </Link>
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label>בחירת קול</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {TTS_VOICE_OPTIONS.map((option) => (
                <div key={option.value} className="space-y-1">
                  <button
                    type="button"
                    onClick={() => setVoice(option.value)}
                    className={cn(
                      'w-full rounded-md border-2 p-3 text-sm text-center transition-colors',
                      voice === option.value
                        ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                        : 'border-input hover:bg-muted/50',
                    )}
                  >
                    {option.label}
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleVoiceSample(option.value)}
                    className="flex items-center justify-center gap-1 w-full text-xs text-muted-foreground hover:text-foreground"
                  >
                    {playingSampleVoice === option.value ? (
                      <Square className="size-3" />
                    ) : (
                      <Volume2 className="size-3" />
                    )}
                    {playingSampleVoice === option.value ? 'עצירה' : 'השמעת דוגמה'}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>אווירת הטקסט</Label>
            <div className="flex flex-wrap gap-2">
              {TTS_MOOD_OPTIONS.map((option) => (
                <Button
                  key={option.value}
                  type="button"
                  size="sm"
                  variant={mood === option.value ? 'default' : 'outline'}
                  onClick={() => setMood(option.value)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
            {mood === 'custom' && (
              <Textarea
                placeholder="תארו את האווירה/הסגנון הרצוי להקראת הטקסט"
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value.slice(0, MAX_CUSTOM_PROMPT_LENGTH))}
                rows={2}
              />
            )}
          </div>

          <div className="space-y-2">
            <Label>מודל</Label>
            <Select value={model} onValueChange={(value) => setModel(value as TtsModel)}>
              <SelectTrigger>
                <SelectValue placeholder="בחר מודל" />
              </SelectTrigger>
              <SelectContent>
                {TTS_MODEL_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>טקסט להמרה</Label>
            <Textarea
              placeholder="הזינו כאן את הטקסט שיש להמיר לדיבור"
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, MAX_TEXT_LENGTH))}
              rows={6}
            />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>עלות משוערת ליצירת הקול: כ-{estimatedUnits.toFixed(1)} יחידות</span>
              <span>{text.length}/{MAX_TEXT_LENGTH}</span>
            </div>
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground">
                תגיות הבעה (לחיצה מוסיפה את התגית לטקסט) - למידע נוסף ראו את המדריך המורחב
              </div>
              <div className="flex flex-wrap gap-2">
                {TTS_AUDIO_TAGS.map((tagOption) => (
                  <Button
                    key={tagOption.tag}
                    type="button"
                    variant="secondary"
                    size="sm"
                    title={tagOption.description}
                    onClick={() => insertAudioTag(tagOption.tag)}
                  >
                    {tagOption.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <Button type="button" onClick={handleSubmit} disabled={!canSubmit || isLoading} className="w-full">
            {isLoading ? 'יוצר דיבור...' : 'המר לדיבור'}
          </Button>

          {audioUrl && (
            <div className="space-y-3 rounded-md border p-3">
              <AudioPlayer src={audioUrl} title="דיבור שנוצר" />
              <div className="flex items-center justify-between">
                <Button type="button" variant="outline" size="sm" onClick={handleDownload}>
                  הורדת קובץ WAV
                </Button>
                {resultInfo && (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-medium text-foreground">
                      יחידות שנוצלו: {resultInfo.units}
                    </span>
                    <span className="text-muted-foreground">משך: {resultInfo.durationSeconds.toFixed(1)} שנ&#39;</span>
                    {/* <span className="text-muted-foreground">טוקנים: {resultInfo.totalTokens}</span> */}
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </PageWrapper>
  );
}
